import { useCallback, useEffect, useState } from 'react';

interface ScrollState {
  isScrolled: boolean;
  isScrolledToBottom: boolean;
}

/**
 * A lightweight hook that only tracks if an element is scrolled or scrolled to bottom.
 * Optimized to minimize re-renders by only updating when boolean states change.
 *
 * @param ref - React ref object pointing to the element to track
 * @returns Object containing isScrolled and isScrolledToBottom booleans
 *
 * @example
 * ```tsx
 * const ref = useRef<HTMLDivElement>(null);
 * const { isScrolled, isScrolledToBottom } = useIsElementScrolled(ref);
 *
 * return (
 *   <div ref={ref} style={{ height: '200px', overflow: 'auto' }}>
 *     {isScrolled && <div>Scrolled!</div>}
 *     {isScrolledToBottom && <div>Reached bottom!</div>}
 *   </div>
 * );
 * ```
 */
export const useIsElementScrolled = (ref: React.RefObject<HTMLElement>): ScrollState => {
  const [scrollState, setScrollState] = useState<ScrollState>({
    isScrolled: false,
    isScrolledToBottom: false,
  });

  const handleScroll = useCallback(() => {
    const element = ref.current;
    if (!element) return;

    const { scrollTop, scrollHeight, clientHeight } = element;
    const newIsScrolled = scrollTop > 0;
    const newIsScrolledToBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1;

    // Only update state if either boolean has changed
    if (
      newIsScrolled !== scrollState.isScrolled ||
      newIsScrolledToBottom !== scrollState.isScrolledToBottom
    ) {
      setScrollState({
        isScrolled: newIsScrolled,
        isScrolledToBottom: newIsScrolledToBottom,
      });
    }
  }, [ref, scrollState.isScrolled, scrollState.isScrolledToBottom]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    element.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => element.removeEventListener('scroll', handleScroll);
  }, [handleScroll, ref]);

  return scrollState;
};
